import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"

dayjs.extend(utc)
dayjs.extend(timezone)

const THIRTY_SIX_HOURS_MS = 36 * 60 * 60 * 1000

/**
 * Advance a dayjs date by one calendar day, safe across DST transitions.
 *
 * dayjs's `add(1, "day")` adds exactly 24 UTC hours. On DST
 * spring-forward days the lost hour means `startOf("day")` can
 * resolve back to the same midnight, causing infinite loops.
 * On fall-back days it can produce duplicate dates.
 *
 * The fix: jump 36 hours via raw UTC milliseconds (guaranteeing we
 * land in the next calendar day regardless of DST direction), extract
 * the YYYY-MM-DD string, then reconstruct midnight via `dayjs.tz()`
 * in creation mode — which resolves the correct UTC offset for that
 * date (unlike `startOf("day")` which preserves the input's offset).
 *
 * Same algorithm as dstSafeLocalizer.js (for react-big-calendar),
 * extracted here so any date-iteration loop can use it.
 *
 * @param {import("dayjs").Dayjs} date - a timezone-aware dayjs object
 * @param {string} timezone - IANA timezone identifier (e.g. "Australia/Melbourne")
 * @returns {import("dayjs").Dayjs} midnight of the next calendar day in the given timezone
 */
export function dstSafeNextDay(date, timezone) {
  const nextDateStr = dayjs(new Date(date.valueOf() + THIRTY_SIX_HOURS_MS))
    .tz(timezone)
    .format("YYYY-MM-DD")
  return dayjs.tz(nextDateStr, timezone)
}
